import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, HelpCircle } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ProductUsageRow } from "@/lib/types";
import { adminLicensesPath } from "@/lib/navigation";
import { useTimezone } from "@/lib/timezone";
import { cn } from "@/lib/utils";

export type ProductUsageSegment = "paid" | "trial" | "combined";
export type ProductUsageWindow = "24h" | "7d" | "30d";

type CapacityRisk = "healthy" | "near" | "at";

type CapacityPresentation = {
  available: boolean;
  text: string;
  utilization: number | null;
  risk: CapacityRisk | null;
};

export function ProductUsageMatrix({
  products,
  segment,
  window,
}: {
  products: ProductUsageRow[];
  segment: ProductUsageSegment;
  window: ProductUsageWindow;
}) {
  const { t } = useTranslation();
  const { formatDateTime } = useTimezone();

  // Keep keyboard-focused metric controls clear of the pinned shortcuts.
  return (
    <section
      className="min-w-0 shrink-0 [&_.console-scrollbar]:scroll-pr-36"
      aria-label={t("usage.matrixAria", "Product usage evidence")}
    >
      <TooltipProvider delay={200} closeDelay={100}>
        <Table
          className="min-w-[1040px] table-fixed [&_thead_th]:px-3 [&_thead_th]:font-sans [&_thead_th]:text-xs [&_thead_th]:normal-case [&_thead_th]:tracking-normal [&_td]:px-3 [&_td]:py-5 [&_td]:align-top"
        >
          <caption className="sr-only">
            {t("usage.tableCaption", "Product usage snapshot")}
          </caption>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead scope="col" className="w-[18%] pl-0!">
                {t("usage.columns.product", "Product")}
              </TableHead>
              <TableHead scope="col" className="w-[11%] text-right">
                <ColumnHeaderWithTooltip
                  title={t("usage.columns.licenses", "Effective Active Licenses")}
                  tooltip={t("usage.tooltips.licenses", "Active Licenses inside the server UTC validity window.")}
                  align="right"
                />
              </TableHead>
              <TableHead scope="col" className="w-[12%] text-right">
                <ColumnHeaderWithTooltip
                  title={t("usage.columns.activations", "Lifecycle-active Activations")}
                  tooltip={t("usage.tooltips.activations", "Activations with Active lifecycle status.")}
                  align="right"
                />
              </TableHead>
              <TableHead scope="col" className="w-[12%] text-right">
                <ColumnHeaderWithTooltip
                  title={t("usage.columns.seen", "Recently Seen Devices")}
                  tooltip={t("usage.tooltips.seen", "Lifecycle-active Activations with successful activation or authenticated token refresh within the selected server UTC window (not application-open time).")}
                  align="right"
                />
                <span className="mt-1 block text-right">{t(`usage.windows.${window}`, window)}</span>
              </TableHead>
              <TableHead scope="col" className="w-[17%]">
                <ColumnHeaderWithTooltip
                  title={t("usage.columns.capacity", "Device capacity")}
                  tooltip={t("usage.tooltips.capacity", "Active devices versus total quota under policy (Current / Limit (%)).")}
                />
              </TableHead>
              <TableHead scope="col" className="w-[18%]">
                <ColumnHeaderWithTooltip
                  title={t("usage.contactLifecycle", "Contact / lifecycle")}
                  tooltip={t("usage.tooltips.contactLifecycle", "Latest successful activation or authenticated refresh, and New / Deactivated Activations in the selected window.")}
                />
              </TableHead>
              <TableHead scope="col" className="sticky right-0 w-[12%] bg-canvas pr-0!">
                {t("usage.shortcuts", "Shortcuts")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id}>
                <th scope="row" className="py-5 pr-3 text-left align-top font-normal">
                  <span className="block wrap-break-word text-base font-medium text-ink">
                    {product.name}
                  </span>
                  <span className="mt-1 block break-all font-mono text-[11px] text-muted">
                    {product.key}
                  </span>
                  <span className={cn("mt-2 inline-flex items-center gap-1.5 text-xs", product.status === "active" ? "text-success" : "text-muted")}>
                    {product.status === "active" ? <span className="size-1.5 rounded-full bg-current" aria-hidden="true" /> : null}
                    {product.status === "active" ? t("common.active", "Active") : t("usage.statusDisabled", "Disabled")}
                  </span>
                </th>
                <TableCell className="text-right">
                  {segment !== "combined" && product.metric_availability.effective_active_licenses
                    ? <span className="font-mono text-lg tabular-nums text-ink">{product.effective_active_licenses}</span>
                    : <span className="text-xs text-muted">{t("usage.unavailable", "Unavailable")}</span>}
                </TableCell>
                <TableCell className="text-right font-mono text-lg tabular-nums text-ink">
                  {product.lifecycle_active_activations}
                </TableCell>
                <TableCell className="text-right font-mono text-lg tabular-nums text-ink">
                  {product.recently_seen_devices}
                </TableCell>
                <TableCell>
                  {segment === "combined"
                    ? <span className="text-xs text-muted">{t("usage.unavailable", "Unavailable")}</span>
                    : <CapacityEvidence presentation={capacityPresentation(product, t)} compact />}
                </TableCell>
                <TableCell className="text-xs">
                  {product.latest_successful_contact
                    ? <time dateTime={product.latest_successful_contact} className="text-ink">{formatDateTime(product.latest_successful_contact)}</time>
                    : <span className="text-muted">{t("usage.noContact", "No successful contact")}</span>}
                  <div className="mt-2 flex flex-wrap gap-x-2 gap-y-1 text-[11px] tabular-nums text-muted" aria-label={t("usage.lifecycleAria", {
                    created: product.new_activations,
                    deactivated: product.deactivated_activations,
                    defaultValue: `New ${product.new_activations}, Deactivated ${product.deactivated_activations}`,
                  })}>
                    <span>{t("usage.lifecycleNew", "New")} {product.new_activations}</span>
                    <span>{t("usage.lifecycleDeactivated", "Deactivated")} {product.deactivated_activations}</span>
                  </div>
                </TableCell>
                <TableCell className="sticky right-0 bg-canvas pr-0!">
                  <WorkflowLinks product={product} segment={segment} window={window} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TooltipProvider>
    </section>
  );
}

function WorkflowLinks({
  product,
  segment,
  window,
}: {
  product: ProductUsageRow;
  segment: ProductUsageSegment;
  window: ProductUsageWindow;
}) {
  const { t } = useTranslation();
  const workflowLinkClass =
    "inline-flex w-fit items-center gap-1 rounded-sm text-xs text-accent underline-offset-4 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus pointer-coarse:min-h-11";

  return (
    <nav className="flex flex-col items-start gap-2" aria-label={t("usage.workflowsAria", {
      product: product.name,
      defaultValue: `${product.name} workflows`,
    })}>
      <Link
        className={workflowLinkClass}
        to={`/admin/products/${encodeURIComponent(product.id)}/usage?segment=${encodeURIComponent(segment)}&window=${encodeURIComponent(window)}`}
        aria-label={t("usage.viewAnalysisFor", { product: product.name, defaultValue: `View analysis for ${product.name}` })}
      >
        {t("usage.analysisShortcut", "Analysis")}
        <ArrowUpRight className="size-3.5 shrink-0" aria-hidden="true" />
      </Link>
      <Link
        className={workflowLinkClass}
        to={adminLicensesPath({ product: product.id })}
        aria-label={t("usage.viewLicensesFor", { product: product.name, defaultValue: `View Licenses for ${product.name}` })}
      >
        {t("usage.licenses", "Licenses")}
        <ArrowUpRight className="size-3.5 shrink-0" aria-hidden="true" />
      </Link>
    </nav>
  );
}

function CapacityEvidence({
  presentation,
  compact = false,
}: {
  presentation: CapacityPresentation;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  if (!presentation.available || presentation.utilization == null || !presentation.risk) {
    return <span className="text-sm text-muted">{presentation.text}</span>;
  }

  const riskText =
    presentation.risk === "at"
      ? t("usage.atCapacity", "At capacity")
      : presentation.risk === "near"
        ? t("usage.nearCapacity", "Capacity nearly full")
        : t("usage.capacityHealthy", "Capacity healthy");

  return (
    <div data-capacity-risk={presentation.risk}>
      <p className={cn("wrap-break-word tabular-nums text-ink", compact ? "text-xs" : "text-sm font-medium")}>
        {presentation.text}
      </p>
      <CapacityMeter percent={presentation.utilization} risk={presentation.risk} />
      <p
        className={cn(
          "mt-1 text-[11px]",
          presentation.risk === "at"
            ? "text-danger"
            : presentation.risk === "near"
              ? "text-warning"
              : "text-muted",
        )}
      >
        {riskText}
      </p>
    </div>
  );
}

function CapacityMeter({
  percent,
  risk,
}: {
  percent: number;
  risk: CapacityRisk;
}) {
  const clamped = Math.min(100, Math.max(0, percent));
  return (
    <div
      className={cn(
        "mt-1.5 h-1.5 w-full max-w-36 overflow-hidden rounded-full",
        risk === "at"
          ? "bg-danger/15"
          : risk === "near"
            ? "bg-warning/15"
            : "bg-line",
      )}
      aria-hidden="true"
    >
      <div
        className={cn(
          "h-full rounded-full",
          risk === "at"
            ? "bg-danger"
            : risk === "near"
              ? "bg-warning"
              : "bg-ink/70",
        )}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

function capacityPresentation(
  product: ProductUsageRow,
  t: ReturnType<typeof useTranslation>["t"],
): CapacityPresentation {
  const utilization = product.device_capacity.utilization_percent;
  if (
    !product.metric_availability.device_capacity ||
    !product.device_capacity.available ||
    utilization == null
  ) {
    return {
      available: false,
      text: t("usage.unavailable", "Unavailable"),
      utilization: null,
      risk: null,
    };
  }

  return {
    available: true,
    text: t("usage.capacityValue", {
      current: product.device_capacity.current,
      limit: product.device_capacity.limit,
      utilization: utilization.toFixed(1),
      defaultValue: `${product.device_capacity.current} / ${product.device_capacity.limit} (${utilization.toFixed(1)}%)`,
    }),
    utilization,
    risk: utilization >= 100 ? "at" : utilization >= 90 ? "near" : "healthy",
  };
}


function ColumnHeaderWithTooltip({
  title,
  tooltip,
  align = "left",
}: {
  title: string;
  tooltip: string;
  align?: "left" | "right";
}) {
  return (
    <div
      className={cn(
        "flex w-full items-center gap-1.5",
        align === "right" ? "justify-end text-right" : "justify-start text-left",
      )}
    >
      <span className="min-w-0 wrap-break-word leading-tight">{title}</span>
      <Tooltip>
        <TooltipTrigger>
          <button
            type="button"
            className="inline-flex size-4 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:text-ink focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-focus"
            aria-label={tooltip}
          >
            <HelpCircle className="size-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs font-normal leading-normal text-left">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
